import type { VisionRuntimeProvenance } from "../services/visualLabService";
import type { LiveGeometryContract } from "./liveGeometry";

export type ScanProcessStatus =
  | "queued"
  | "running"
  | "done"
  | "error"
  | "skipped";

export interface ScanProcessStep {
  id: string;
  label: string;
  status: ScanProcessStatus;
  detail: string | null;
  durationMs?: number | null;
}

export interface ScanOcrInputEvidence {
  width: number;
  height: number;
  sha256: string;
  psm: number;
  whitelist: string;
}

export interface ScanOcrPassResult {
  pass: string;
  text: string;
  rawText: string;
  confidence: number | null;
  durationMs: number;
  score: number;
  inputEvidence?: ScanOcrInputEvidence;
}

export type ScanStorageAddressSubcellName = "building" | "cabinet" | "compartment" | "subCompartment";

export interface ScanStorageAddressSubcellResult {
  name: ScanStorageAddressSubcellName;
  label: "Gebäude" | "Schrank" | "Fach" | "Unterfach";
  rawText: string;
  decodedText: string;
  confidence: number | null;
  durationMs: number;
  valid: boolean;
  correctionsApplied: string[];
  rectangle: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  inputEvidence?: ScanOcrInputEvidence;
}

export interface ScanStorageAddressDecoderDetails {
  contractVersion: "storage-address-four-subcell-decoder-v1";
  source: "SR26_VECTOR_TEMPLATE_SUBCELLS";
  attempted: boolean;
  selected: boolean;
  valid: boolean;
  candidate: string;
  meanConfidence: number | null;
  durationMs: number;
  reason: string;
  cells: ScanStorageAddressSubcellResult[];
}

export interface ScanOcrFieldResult {
  field: "materialText" | "materialType" | "materialNumber" | "storageAddress";
  label: string;
  text: string;
  rawText: string;
  confidence: number | null;
  durationMs?: number | null;
  rectangle: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  roiSource?: "configured" | "grid-anchored";
  selectedPass?: string | null;
  passCount?: number;
  passes?: ScanOcrPassResult[];
  inputEvidence?: ScanOcrInputEvidence;
  storageAddressDecoder?: ScanStorageAddressDecoderDetails | null;
}

export interface ScanOcrGridAnchoringDetails {
  applied: boolean;
  horizontalMatches: number;
  verticalMatches: number;
  horizontalScale: number | null;
  horizontalOffset: number | null;
  verticalScale: number | null;
  verticalOffset: number | null;
  meanResidual: number | null;
  reason: string;
}

export interface ScanOcrMasterResolutionField {
  field: "materialText" | "materialType" | "storageAddress";
  ocrValue: string;
  resolvedValue: string;
  source: "ocr" | "master";
  reason: string;
  confidence: number | null;
  similarity: number | null;
}

export interface ScanOcrMasterResolutionDetails {
  attempted: boolean;
  materialNumber: string;
  materialFound: boolean;
  masterDataSource: string | null;
  resolvedFieldCount: number;
  fields: ScanOcrMasterResolutionField[];
  error: string | null;
}

export interface ScanOcrDetails {
  engine: "tesseract.js";
  languages: string;
  status: "idle" | "running" | "done" | "error";
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  progress: number;
  fields: ScanOcrFieldResult[];
  gridAnchoring?: ScanOcrGridAnchoringDetails | null;
  masterResolution?: ScanOcrMasterResolutionDetails | null;
  parallelShadow?: ScanOcrParallelShadowEvidence | null;
  parallelProduction?: ScanOcrParallelProductionEvidence | null;
  error: string | null;
}

export interface ScanOcrParallelShadowTask {
  executionId: string;
  workerId: string;
  field: ScanOcrFieldResult["field"];
  executionContext: "webworker";
  transferMode: "shared-canvas-read";
  startedOffsetMs: number;
  finishedOffsetMs: number;
  durationMs: number;
}

export interface ScanOcrParallelShadowFieldParity {
  field: ScanOcrFieldResult["field"];
  textMatch: boolean;
  confidenceMatch: boolean;
  selectedPassMatch: boolean;
  passSequenceMatch: boolean;
  roiShaMatch: boolean;
  storageDecoderMatch: boolean;
  serialText: string;
  parallelText: string;
  serialConfidence: number | null;
  parallelConfidence: number | null;
  serialSelectedPass: string | null;
  parallelSelectedPass: string | null;
  serialRoiSha256: string | null;
  parallelRoiSha256: string | null;
}

export interface ScanOcrParallelShadowEvidence {
  schemaVersion: 1;
  contractVersion: "same-raw-serial-parallel-shadow-v1";
  profileId: "ocr-2-worker-2plus2-shadow-v1";
  status: "skipped" | "pass" | "mismatch" | "error";
  recognitionRunId: string | null;
  startedAtUtc: string;
  finishedAtUtc: string;
  productionInfluenced: false;
  productionExecutionProfile: "serial-production-v1";
  upstreamMode: "shared-serial-detection-rectification";
  rawDecodedSha256: string | null;
  analysisInputSha256: string | null;
  rectificationInputRgbaSha256: string | null;
  rectificationOutputRgbaSha256: string | null;
  rectifiedFileSha256: string | null;
  requestedWorkers: 2;
  createdWorkers: number;
  effectiveWorkers: number;
  activeWorkersPeak: number;
  workersTerminated: boolean;
  workersRetainedForReuse: boolean;
  workerPoolWarm: boolean;
  workerPoolReused: boolean;
  workerPoolReuseCount: number;
  mergeOrder: ScanOcrFieldResult["field"][];
  workerStartupMs: number | null;
  serialWallMs: number | null;
  parallelWallMs: number | null;
  estimatedSpeedup: number | null;
  overlapMs: number | null;
  overlapRatio: number | null;
  tasks: ScanOcrParallelShadowTask[];
  fieldParity: ScanOcrParallelShadowFieldParity[];
  parity: {
    sameFileObject: boolean;
    sharedDetectionGeometry: boolean;
    sharedRectification: boolean;
    gridAnchoringMatch: boolean | null;
    allRoiShaMatch: boolean | null;
    allTextMatch: boolean | null;
    allConfidenceMatch: boolean | null;
    allPassSelectionMatch: boolean | null;
    finalFourFieldsMatch: boolean | null;
    recognitionParityPass: boolean | null;
  };
  fallbackReason: string | null;
  error: string | null;
}



export interface ScanOcrParallelProductionEvidence {
  schemaVersion: 1;
  contractVersion: "gated-production-final-ocr-v1";
  profileId: "pv9-parallel-final-ocr-production-v1" | "serial-production-v1";
  status: "not_eligible" | "serial_fallback" | "parallel";
  fullFidelityPreserved: true;
  productionInfluenced: boolean;
  activationGateSatisfied: boolean;
  admissionAllowed: boolean;
  probeReady: boolean;
  poolsWarm: boolean;
  sessionFuseTripped: boolean;
  reason: string | null;
  requestedWorkers: 2;
  effectiveWorkers: number;
  activeWorkersPeak: number;
  workerStartupMs: number | null;
  parallelWallMs: number | null;
  overlapMs: number | null;
  overlapRatio: number | null;
  mergeOrder: ScanOcrFieldResult["field"][];
  tasks: ScanOcrParallelShadowTask[];
  serialFallbackUsed: boolean;
  error: string | null;
}

export interface ScanRectificationTimingBreakdown {
  totalMs: number | null;
  inputPrepareMs: number | null;
  inputPrepareMode?: "inline" | "detection-overlap";
  inputPrepareCriticalPathWaitMs?: number | null;
  inputShaMs: number | null;
  inputShaCriticalPathWaitMs?: number | null;
  inputArchiveElapsedMs: number | null;
  inputArchiveMode: "inline" | "post-step2-deferred";
  inputArchiveCriticalPathWaitMs: number | null;
  cornerRefinementMs: number | null;
  workerRoundTripMs: number | null;
  outputEncodeMs: number | null;
  rerankOcrMs: number | null;
  templateEvaluationMs: number | null;
  nonPlanarRescueMs: number | null;
  outputShaMs: number | null;
  candidateProbes: number;
}

export interface ScanOpenCvParallelShadowTask {
  executionId: string;
  workerId: string;
  phase: "serial" | "parallel";
  candidateId: string;
  candidateIndex: number | null;
  productionSelected: boolean;
  executionContext: "webworker";
  transferMode: "transferable-arraybuffer-copy";
  transferPrepMs: number;
  startedOffsetMs: number;
  finishedOffsetMs: number;
  durationMs: number;
  outputSha256: string;
  outputWidth: number;
  outputHeight: number;
  timings: {
    reconstructImageDataMs: number;
    inputToMatMs: number;
    homographyMs: number;
    warpPerspectiveMs: number;
    readbackMs: number;
    sha256Ms: number;
    workerTotalMs: number;
  };
}

export interface ScanCriticalPathCandidateBatchParity {
  candidateIndex: number;
  rectifiedShaMatch: boolean;
  materialNumberMatch: boolean;
  confidenceMatch: boolean;
  templateScoreMatch: boolean;
  templateResidualMatch: boolean;
  rerankScoreMatch: boolean;
  serialMaterialNumber: string;
  parallelMaterialNumber: string;
  serialConfidence: number | null;
  parallelConfidence: number | null;
  serialTemplateScore: number | null;
  parallelTemplateScore: number | null;
  serialTemplateResidual: number | null;
  parallelTemplateResidual: number | null;
  serialRerankScore: number;
  parallelRerankScore: number;
}

export interface ScanCriticalPathCandidateBatchShadowEvidence {
  schemaVersion: 1;
  contractVersion: "full-fidelity-critical-path-candidate-batch-shadow-v1";
  profileId: "pv6-topk-rectify-template-rerank-ocr-2worker-shadow-v1";
  status: "skipped" | "pass" | "mismatch" | "error";
  productionInfluenced: false;
  fullFidelityPreserved: true;
  candidateCount: number;
  productionCandidateBatchWallMs: number | null;
  parallelCandidateBatchWallMs: number | null;
  estimatedSpeedup: number | null;
  outputEncodeWallMs: number | null;
  rerankOcrWallMs: number | null;
  templateEvaluationWallMs: number | null;
  selectedCandidateIndexSerial: number | null;
  selectedCandidateIndexParallel: number | null;
  deterministicWinnerMatch: boolean | null;
  candidateParity: ScanCriticalPathCandidateBatchParity[];
  parallelParityPass: boolean | null;
  fallbackReason: string | null;
  error: string | null;
}

export interface ScanOpenCvParallelShadowEvidence {
  schemaVersion: 1;
  contractVersion: "same-input-opencv-parallel-shadow-v1";
  profileId: "opencv-2-worker-candidate-batch-shadow-v1";
  status: "skipped" | "pass" | "mismatch" | "error";
  recognitionRunId: string | null;
  startedAtUtc: string;
  finishedAtUtc: string;
  productionInfluenced: false;
  productionExecutionProfile: "serial-production-v1";
  sourceMode: "archived-production-rectification-rgba";
  decodeMode: "createImageBitmap-canvas" | "html-image-canvas" | null;
  executionContext: "main-thread+webworker";
  transferMode: "transferable-arraybuffer-copy";
  sourceInputRgbaSha256: string | null;
  sourceInputRgbaShaMatch: boolean | null;
  productionOutputRgbaSha256: string | null;
  requestedWorkers: 2;
  createdWorkers: number;
  effectiveWorkers: number;
  activeWorkersPeak: number;
  workersTerminated: boolean;
  workersRetainedForReuse: boolean;
  workerPoolWarm: boolean;
  workerPoolReused: boolean;
  workerPoolReuseCount: number;
  candidateCount: number;
  workerStartupMs: number | null;
  inputDecodeMs: number | null;
  serialWallMs: number | null;
  parallelWallMs: number | null;
  estimatedSpeedup: number | null;
  overlapMs: number | null;
  overlapRatio: number | null;
  productionStageTotalMs: number | null;
  serialTasks: ScanOpenCvParallelShadowTask[];
  parallelTasks: ScanOpenCvParallelShadowTask[];
  parity: {
    allRectifiedShaMatch: boolean | null;
    productionSelectedOutputMatch: boolean | null;
    deterministicOrderMatch: boolean | null;
    sameInputRgba: boolean | null;
    parallelParityPass: boolean | null;
  };
  criticalPathCandidateBatch: ScanCriticalPathCandidateBatchShadowEvidence | null;
  fallbackReason: string | null;
  error: string | null;
}


export interface ScanParallelProductionCandidateBatchEvidence {
  schemaVersion: 1;
  contractVersion: "gated-production-candidate-batch-v1";
  profileId: "pv8-parallel-candidate-batch-production-v1" | "serial-production-v1";
  status: "not_eligible" | "serial_fallback" | "parallel";
  fullFidelityPreserved: true;
  productionInfluenced: boolean;
  activationGateSatisfied: boolean;
  admissionAllowed: boolean;
  probeReady: boolean;
  poolsWarm: boolean;
  sessionFuseTripped: boolean;
  reason: string | null;
  candidateCount: number;
  selectedCandidateIndex: number | null;
  workerStartupMs: number | null;
  parallelWallMs: number | null;
  rectifyWallMs: number | null;
  outputEncodeWallMs: number | null;
  rerankOcrWallMs: number | null;
  templateEvaluationWallMs: number | null;
  effectiveWorkers: number;
  overlapMs: number | null;
  overlapRatio: number | null;
  serialFallbackUsed: boolean;
  error: string | null;
}

export interface ScanParallelProductionNonPlanarRescueEvidence {
  schemaVersion: 1;
  contractVersion: "gated-production-non-planar-rescue-v1";
  profileId: "pv12-parallel-non-planar-rescue-production-v1" | "serial-production-v1";
  status: "not_needed" | "not_eligible" | "serial_fallback" | "parallel";
  fullFidelityPreserved: true;
  productionInfluenced: boolean;
  hardCase: "PLANAR" | "MILD_CURVATURE" | "FOLD_KINK" | null;
  candidateGenerated: boolean;
  candidateApplied: boolean;
  requestedWorkers: 2;
  effectiveWorkers: number;
  workerStartupMs: number | null;
  parallelDewarpWallMs: number | null;
  parallelOcrWallMs: number | null;
  overlapMs: number | null;
  overlapRatio: number | null;
  transferMode: "transferable-arraybuffer-copy" | null;
  serialFallbackUsed: boolean;
  reason: string | null;
  error: string | null;
}


export interface ScanParallelRuntimeSoakProfileSummary {
  samples: number;
  fullFidelitySamples: number;
  passes: number;
  mismatches: number;
  errors: number;
  skipped: number;
  warmReusedFullFidelityPasses: number;
  parallelOverlapFullFidelityPasses: number;
}

export interface ScanParallelRuntimeSoakSnapshot {
  schemaVersion: 1;
  contractVersion: "browser-parallel-runtime-soak-v1";
  maxSamples: number;
  requiredWarmFullFidelitySamples: number;
  totalSamples: number;
  fullFidelitySamples: number;
  opencv: ScanParallelRuntimeSoakProfileSummary;
  ocr: ScanParallelRuntimeSoakProfileSummary;
  criticalPathCandidateBatch: ScanParallelRuntimeSoakProfileSummary;
  lifecycle: {
    opencv: Record<"pagehide_terminated" | "visibility_hidden_terminated" | "runtime_error_recycle" | "pool_ready", number>;
    ocr: Record<"pagehide_terminated" | "visibility_hidden_terminated" | "runtime_error_recycle" | "pool_ready", number>;
  };
  acceptance: {
    workerFailureRecovery: { attempts: number; passes: number; lastAtUtc: string | null; lastError: string | null };
    memoryPressureFallback: { attempts: number; passes: number; lastAtUtc: string | null; lastReason: string | null };
  };
  qualification: {
    contractVersion: "browser-parallel-runtime-release-certification-v1";
    releaseStatus: "qualification_pending" | "certified" | "revoked";
    releaseProfileMatched: boolean;
    releaseProfileId: string | null;
    localSoakReady: boolean;
    source: "release_certificate" | "local_development_soak" | "none";
    perUserSoakRequired: false;
    reason: string;
  };
  gates: {
    cleanParityWindow: boolean;
    warmFullFidelityReady: boolean;
    criticalPathCandidateBatchReady: boolean;
    backgroundRecoveryEvidence: boolean;
    workerFailureRecoveryEvidence: boolean;
    memoryPressureFallbackEvidence: boolean;
    productionActivationAllowed: boolean;
    state: "collecting" | "soak_candidate" | "ready" | "blocked";
  };
}

export interface ScanGridGeometryDiagnostics {
  checked: boolean;
  accepted: boolean;
  areaRatio: number | null;
  aspectRatio: number | null;
  geometryScore: number | null;
  convex: boolean | null;
  plausibleBounds: boolean | null;
  reason: string | null;
}

export interface ScanGridAxisDiagnostics {
  available: boolean;
  scale: number | null;
  offset: number | null;
  score: number | null;
  residual: number | null;
  matches: { reference: number; observed: number }[];
}

export interface ScanGridHypothesisDiagnostics {
  index: number;
  horizontalAxis: ScanGridAxisDiagnostics;
  verticalAxis: ScanGridAxisDiagnostics;
  reconstructionCorners: { x: number; y: number }[];
  geometry: ScanGridGeometryDiagnostics;
  targetDistanceRatio: number | null;
  quickWarp: ScanQuickWarpDiagnostics;
  preliminaryScore: number;
  combinedScore: number;
  selected: boolean;
}

export interface ScanOrbDiagnostics {
  available: boolean;
  phase: string;
  error: string | null;
  sourceKeypoints: number;
  targetKeypoints: number;
  sourceDescriptorRows: number;
  targetDescriptorRows: number;
  rawMatches: number;
  ratioMatches: number;
  geometricMatches: number;
  balancedMatches: number;
  inliers: number;
  usedKnn: boolean;
  usedGeometricPrior: boolean;
  usedSpatialBalancing: boolean;
  confidenceScore: number;
  baseConfidenceScore: number;
  reprojectionError: number | null;
  spatial: Record<string, unknown>;
  decisionMode: "refinement" | "recovery" | "mapping" | "none";
  cornerShiftRatio: number | null;
  accepted: boolean;
  rejectionReason: string | null;
  preprocessing: "clahe" | "equalizeHist" | "grayscale";
  candidateCorners: { x: number; y: number }[] | null;
  candidateGeometry: ScanGridGeometryDiagnostics;
  internalMinAreaRatio: number | null;
  internalGeometryScore: number | null;
}


export interface ScanQuickWarpDiagnostics {
  checked: boolean;
  accepted: boolean;
  horizontalSupported: number;
  verticalSupported: number;
  averageSupport: number;
  horizontalSupports?: number[];
  verticalSupports?: number[];
  horizontalReferencePositions?: number[];
  verticalReferencePositions?: number[];
  minimumLineRatio?: number;
  reason: string | null;
}

export interface ScanCandidatePoolDiagnostics {
  index: number;
  source: string;
  method: string;
  corners: { x: number; y: number }[];
  baseScore: number;
  geometry: ScanGridGeometryDiagnostics;
  targetDistanceRatio: number | null;
  referenceFit: ScanQuickWarpDiagnostics;
  referenceScore: number;
  combinedScore: number;
  accepted: boolean;
  selected: boolean;
  rejectionReason: string | null;
}

export interface ScanTargetGuidanceDiagnostics {
  available: boolean;
  targetCorners: { x: number; y: number }[] | null;
  selectedCornerDistanceRatio: number | null;
  rescueUsed: boolean;
  rescueMethod: string | null;
}
export interface ScanFinalGeometryDiagnostics {
  checked: boolean;
  accepted: boolean;
  horizontalSupported: number;
  verticalSupported: number;
  averageSupport: number;
  horizontalSupports?: number[];
  verticalSupports?: number[];
  horizontalReferencePositions?: number[];
  verticalReferencePositions?: number[];
  minimumLineRatio?: number;
  reason: string | null;
}

export interface ScanDiagnosticIdentity {
  withdrawalNumber: number | null;
  serverId: number | null;
  clientUuid: string | null;
}

export interface ScanParallelismProbeSnapshot {
  schemaVersion: 1;
  contractVersion: "browser-parallelism-probe-v1";
  status: "not_run" | "running" | "ready" | "unsupported" | "error";
  measuredAtUtc: string | null;
  logicalCpuCount: number | null;
  requestedWorkers: 2;
  createdWorkers: number;
  workerApiAvailable: boolean;
  offscreenCanvasAvailable: boolean;
  createImageBitmapAvailable: boolean;
  crossOriginIsolated: boolean;
  sharedArrayBufferAvailable: boolean;
  atomicsAvailable: boolean;
  wasmAvailable: boolean;
  wasmPthreadsPrerequisitesMet: boolean;
  sequentialWallMs: number | null;
  parallelWallMs: number | null;
  estimatedSpeedup: number | null;
  classification: "pending" | "likely_parallel" | "likely_serialized" | "inconclusive" | "unsupported" | "error";
  checksumMatch: boolean | null;
  fallbackReason: string | null;
  error: string | null;
}

export interface ScanRecognitionRuntimeTelemetry {
  schemaVersion: 1;
  contractVersion: "device-browser-runtime-telemetry-v1";
  capturedAtUtc: string;
  device: {
    instanceId: string | null;
    label: string;
    deviceClass: "iphone" | "ipad" | "android-phone" | "android-tablet" | "windows" | "mac" | "other";
    platform: string | null;
    logicalCpuCount: number | null;
    deviceMemoryGb: number | null;
    installedPwa: boolean;
    screenWidth: number | null;
    screenHeight: number | null;
    devicePixelRatio: number | null;
  };
  browser: {
    family: "safari" | "chrome" | "edge" | "firefox" | "samsung-internet" | "other";
    version: string | null;
    majorVersion: string | null;
    engine: "webkit" | "blink" | "gecko" | "unknown";
    userAgent: string;
  };
  runtime: {
    runtimeApi: string | null;
    productionPipelineSha256: string;
    bundleId: string | null;
    bundleVersion: string | null;
    recognitionRevision: number | null;
    detectionBackend: "opencv-js-dedicated-worker";
    detectionWorkerState: "cold" | "warm";
    ocrBackend: string;
    ocrPreprocessRuntime: string | null;
    ocrWorkerState: "cold" | "starting" | "warm";
    cnnEnabled: boolean;
    cnnRuntime: string | null;
    webGpuAvailable: boolean;
    crossOriginIsolated: boolean;
    sharedArrayBufferAvailable: boolean;
    wasmAvailable: boolean;
  };
}

export interface ScanRecognitionTiming {
  schemaVersion: 1;
  contractVersion: "shutter-step2-latency-v1";
  recognitionRunId: string;
  shutterAtUtc: string;
  step2DataReadyAtUtc: string | null;
  shutterToStep2DataReadyMs: number | null;
  durationClock: "performance.now";
  runtimeTemperature: "cold" | "warm" | "unknown";
  runtimeTemperatureBasis: string;
  runtimeTelemetry?: ScanRecognitionRuntimeTelemetry | null;
}

export interface ScanCriticalPathOrchestrationEvidence {
  schemaVersion: 1;
  contractVersion: "pv13-full-fidelity-critical-path-overlap-v1" | "pv15-full-fidelity-performance-freeze-v1";
  fullFidelityPreserved: true;
  controlImageMode: "overlapped-same-artifact";
  controlImageElapsedMs: number | null;
  controlImageCriticalPathWaitMs: number | null;
  qualityElapsedMs: number | null;
  detectionElapsedMs: number | null;
  qualityDetectionOverlapMs: number | null;
  detectionEvidenceElapsedMs?: number | null;
  detectionEvidenceCriticalPathWaitMs?: number | null;
  detectionEvidenceMode?: "detection-overlap" | "post-detection-constrained" | null;
  rectificationInputPrepareMode?: "inline" | "detection-overlap";
  rectificationInputPrepareElapsedMs?: number | null;
  rectificationInputCriticalPathWaitMs?: number | null;
  speculativeFinalOcrStatus?: "not_started" | "ready_baseline" | "failed" | "discarded_rescue_applied" | "reused";
  speculativeFinalOcrElapsedMs?: number | null;
  speculativeFinalOcrCriticalPathWaitMs?: number | null;
}

export interface ScanCaptureDetails {
  identity?: ScanDiagnosticIdentity;
  capturedAt: string;
  recognitionTiming?: ScanRecognitionTiming;
  criticalPathOrchestration?: ScanCriticalPathOrchestrationEvidence | null;
  parallelRuntimeSoak?: ScanParallelRuntimeSoakSnapshot | null;
  captureRuntimeContract?: {
    contractVersion: "camera-shutter-contract-v1" | "camera-shutter-contract-v2" | "camera-shutter-contract-v3" | "camera-shutter-contract-v4";
    runtimeState: "COLD" | "INITIALIZING" | "READY" | "FAILED";
    canCapture: boolean;
    captureRecommended: boolean;
    warmStartAvailable: boolean;
    detectionWorkerState: "cold" | "warm";
    ocrWorkerState: "cold" | "starting" | "warm";
    prewarmError: string | null;
  };
  geometryContract?: LiveGeometryContract;
  captureSource: "image-capture" | "video-frame";
  processingSteps: ScanProcessStep[];
  quality: {
    sharpness: number;
    brightness: number;
    contrast: number;
    highlightClippingRatio?: number;
    highlightP95?: number;
    sharpnessLevel?: "bad" | "warning" | "good";
    brightnessLevel?: "bad" | "warning" | "good";
    contrastLevel?: "bad" | "warning" | "good";
  } | null;
  cameraDiagnostics?: {
    width: number | null;
    height: number | null;
    frameRate: number | null;
    aspectRatio: number | null;
    facingMode: string | null;
    resizeMode: string | null;
    focusModes: string[];
    activeFocusMode: string | null;
    activeFocusDistance: number | null;
    activePointsOfInterest: { x: number; y: number }[];
    focusModeConstraintSupported: boolean;
    pointsOfInterestConstraintSupported: boolean;
    focusDistanceConstraintSupported: boolean;
    centerFocusAvailable: boolean;
    centerFocusVerified: boolean;
    centerFocusReported: boolean;
    centerFocusSharpnessConfirmed: boolean;
    focusSharpnessBefore: number | null;
    focusSharpnessAfter: number | null;
    focusSharpnessSamples: number;
    focusLocked: boolean;
    focusRuntimeMode: string;
    lastFocusAction: string | null;
    lastFocusError: string | null;
    exposureMode: string | null;
    exposureCompensation: number | null;
    exposureTime: number | null;
    iso: number | null;
    whiteBalanceMode: string | null;
    colorTemperature: number | null;
    zoom: number | null;
    requestedFocusPoint: { x: number; y: number } | null;
    torchSupported: boolean;
    trackLabel: string;
    selectedDeviceLabel: string;
    videoDeviceCount: number;
    isFourK: boolean;
  };
  mapping: {
    found: boolean;
    method: string;
    matchCount: number;
    inlierCount: number;
    inlierRatio: number;
    confidence: number;
    photoCorners?: { x: number; y: number }[];
    previewWidth?: number | null;
    previewHeight?: number | null;
    photoAnalysisWidth?: number | null;
    photoAnalysisHeight?: number | null;
    searchRoi?: {
      left: number;
      top: number;
      right: number;
      bottom: number;
      pixelLeft?: number | null;
      pixelTop?: number | null;
      pixelRight?: number | null;
      pixelBottom?: number | null;
      pixelWidth?: number | null;
      pixelHeight?: number | null;
      adaptiveHorizontalPadding?: number | null;
      adaptiveVerticalPadding?: number | null;
      mappingQuality?: number | null;
      adaptiveReason?: string | null;
    };
    gridCoarseUsed?: boolean;
    previewGridScore?: number;
    photoGridScore?: number;
    orbDiagnostics?: Record<string, unknown>;
  } | null;
  detection: {
    found: boolean;
    finalGeometryAccepted: boolean;
    score: number;
    detectionMethod: string;
    processingTimeMs: number;
    phaseTimings?: {
      inputToMatMs: number;
      preprocessMs: number;
      gridMs: number;
      orbMs: number;
      structureMs: number;
      contourMs: number;
      houghMs: number;
      candidateEvaluationInclusiveMs: number;
      finalizationMs: number;
      totalMs: number;
    };
    parallelDetectionCandidateEvaluation?: {
      schemaVersion: 1;
      contractVersion: "pv14-detection-candidate-evaluation-parallel-v1";
      profileId: "pv14-parallel-detection-candidate-evaluation-v1";
      status: "parallel" | "serial" | "fallback";
      fullFidelityPreserved: true;
      productionInfluenced: boolean;
      requestedWorkers: 2;
      effectiveWorkers: 1 | 2;
      poolWarm: boolean;
      workerStartupMs: number;
      candidateCount: number;
      localTaskCount: number;
      remoteTaskCount: number;
      parallelWallMs: number | null;
      serialEquivalentMs: number | null;
      estimatedSpeedup: number | null;
      overlapMs: number | null;
      overlapRatio: number | null;
      transferMode: "structured-clone-image-data" | "none";
      serialFallbackUsed: boolean;
      reason: string | null;
      error: string | null;
    };
    imageWidth: number;
    imageHeight: number;
    analysisImageWidth?: number;
    analysisImageHeight?: number;
    analysisInputSha256?: string | null;
    decodedInputSha256?: string | null;
    analysisResizeMethod?: string;
    areaRatio: number | null;
    rectangularity: number | null;
    contourCount: number;
    candidateCount: number;
    houghLineCount: number;
    exclusionStats: Record<string, number>;
    gridScore: number;
    gridHorizontalLines: number;
    gridVerticalLines: number;
    gridHorizontalPositions?: number[];
    gridVerticalPositions?: number[];
    gridMatchedHorizontal: number;
    gridMatchedVertical: number;
    homographyInliers: number;
    gridGeometry: ScanGridGeometryDiagnostics;
    gridHomographyCorners: { x: number; y: number }[] | null;
    gridReconstructionUsed: boolean;
    gridReconstructionScore: number;
    gridReconstructionReason: string | null;
    gridReconstructionCandidateCorners: { x: number; y: number }[] | null;
    gridReconstructionGeometry: ScanGridGeometryDiagnostics;
    gridHorizontalAxis: ScanGridAxisDiagnostics;
    gridVerticalAxis: ScanGridAxisDiagnostics;
    gridHypotheses: ScanGridHypothesisDiagnostics[];
    orbMatches: number;
    orbInliers: number;
    orbInlierRatio: number;
    orbScore: number;
    orbDiagnostics: ScanOrbDiagnostics;
    referenceUsed: boolean;
    rejectionChain: string[];
    finalGeometry: ScanFinalGeometryDiagnostics;
    quickWarp: ScanQuickWarpDiagnostics;
    targetGuidance: ScanTargetGuidanceDiagnostics;
    corners: { x: number; y: number }[] | null;
    rawCorners?: { x: number; y: number }[] | null;
    candidatePool: ScanCandidatePoolDiagnostics[];
    cornerRefinement?: {
      applied: boolean;
      averageShiftPx: number;
      maxShiftPx: number;
      edgeScores: number[];
    } | null;
    topKRerank?: {
      attempted: boolean;
      changedWinner: boolean;
      selectedCandidateIndex: number | null;
      probes: {
        candidateIndex: number;
        geometryScore: number;
        materialNumber: string;
        confidence: number | null;
        templateScore: number;
        templateResidual: number | null;
        rerankScore: number;
      }[];
    } | null;
    candidates: {
      index: number;
      areaRatio?: number;
      rectangularity?: number;
      aspectRatio?: number;
      score?: number;
      eligibleAsOuterLabel: boolean;
      corners: { x: number; y: number }[];
    }[];
    visionSnapshot?: Omit<VisionRuntimeProvenance, "source"> & {
      source: "database" | "default" | "override";
    };
  } | null;
  rectification: {
    used: boolean;
    width: number | null;
    height: number | null;
    error: string | null;
    sourceMode?: "cropped-analysis" | "raw-photo";
    inputWidth?: number | null;
    inputHeight?: number | null;
    inputRgbaSha256?: string | null;
    outputRgbaSha256?: string | null;
    runtime?: "label-detection-worker-opencv-1800x432-v1" | null;
    inputCorners?: { x: number; y: number }[] | null;
    minimumAreaRatio?: number | null;
    areaRatio?: number | null;
    aspectRatio?: number | null;
    timing?: ScanRectificationTimingBreakdown | null;
    parallelShadow?: ScanOpenCvParallelShadowEvidence | null;
    productionParallelCandidateBatch?: ScanParallelProductionCandidateBatchEvidence | null;
    productionParallelNonPlanarRescue?: ScanParallelProductionNonPlanarRescueEvidence | null;
  };
  debugLines: string[];
  ocr: ScanOcrDetails | null;
  totalProcessingTimeMs?: number | null;
}

export interface RecognizedLabelData {
  materialText: string;
  materialType: string;
  materialNumber: string;
  orderInfo: string;
  storageAddress: string;
}

export function createEmptyRecognizedLabelData(): RecognizedLabelData {
  return {
    materialText: "",
    materialType: "",
    materialNumber: "",
    orderInfo: "",
    storageAddress: "",
  };
}

export function createEmptyOcrDetails(): ScanOcrDetails {
  return {
    engine: "tesseract.js",
    languages: "deu+eng",
    status: "idle",
    startedAt: null,
    finishedAt: null,
    durationMs: null,
    progress: 0,
    fields: [],
    gridAnchoring: null,
    masterResolution: null,
    parallelShadow: null,
    parallelProduction: null,
    error: null,
  };
}
